<script setup>
import { computed, nextTick, ref } from 'vue'
import { useDisplay } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { extractDate, extractTimeFromDateString, getDateAtMidnight } from '@/utils/datetime'
import CopyableField from "@/components/CopyableField.vue"

/**
 * Single ticket / pass (karnet) presented as a card.
 *
 * Data-source agnostic: feed it the voucher entity as returned by
 * customerStore.getCustomerVouchers(clientId, 2) — no mapping needed.
 */
const props = defineProps({
  ticket: { type: Object, required: true },

  // Usage history for THIS ticket — parent fetches it lazily on `usagesRequest`
  usages: { type: Array, default: () => [] },
  usagesLoading: { type: Boolean, default: false },
  isLoading: { type: Boolean, default: false },
})

const emit = defineEmits(['usagesRequest', 'editTimeout', 'calendarClick'])

const { t } = useI18n()
const { mobile, smAndUp, mdAndUp, mdAndDown } = useDisplay()

// Every data column of the summary row shares one width - and drops the cap once they stack
const columnStyle = computed(() => {
  if (mobile.value) return undefined

  const size = mdAndDown.value ? '170px' : '195px'

  return { minInlineSize: size, maxInlineSize: size }
})

const timeoutDate = computed(() => (props.ticket.timeout ? new Date(props.ticket.timeout) : null))

// The upcoming-expiry chip is shown up to three calendar months before the timeout
const EXPIRES_SOON_MONTHS = 3

// Timeout normalised to midnight, so day arithmetic ignores the "23:59:59" the backend sends
const timeoutDay = computed(() => {
  if (!timeoutDate.value) return null

  const day = new Date(timeoutDate.value)

  day.setHours(0, 0, 0, 0)

  return day
})

// Calendar days left until the pass expires — drives the "expires soon" chip
const daysLeft = computed(() => (timeoutDay.value
  ? Math.round((timeoutDay.value - getDateAtMidnight()) / 86400000)
  : null))

const isExpired = computed(() => daysLeft.value !== null && daysLeft.value < 0)

// Whole calendar months to the timeout: 3 Sep -> 3 Nov is 2 months, 3 Sep -> 2 Nov is 1
const monthsLeft = computed(() => {
  if (!timeoutDay.value) return null

  const today = getDateAtMidnight()

  const months = (timeoutDay.value.getFullYear() - today.getFullYear()) * 12
    + (timeoutDay.value.getMonth() - today.getMonth())

  // the day of the month decides whether the last month is complete
  return Math.max(timeoutDay.value.getDate() < today.getDate() ? months - 1 : months, 0)
})

// Calendar limit, not 90 days — three months from today lands on the same day of month
const expiresSoon = computed(() => {
  if (!timeoutDay.value || daysLeft.value < 0) return false

  const limit = getDateAtMidnight()

  limit.setMonth(limit.getMonth() + EXPIRES_SOON_MONTHS)

  return timeoutDay.value <= limit
})

// Below a month we count in days, above that in whole months
const expiresSoonLabel = computed(() => {
  if (!expiresSoon.value) return ''
  if (daysLeft.value === 0) return t('expiresToday')
  if (monthsLeft.value < 1) return t('expiresInDays', { count: daysLeft.value })

  return t('expiresInMonths', { count: monthsLeft.value })
})

const services = computed(() => (props.ticket.services || []).map(service => {
  const uses = Number(service.uses) || 0
  const usesLeft = Number(service.usesLeft) || 0
  const used = Math.max(uses - usesLeft, 0)

  return {
    ...service,
    uses,
    usesLeft,
    used,
    percent: uses ? (used / uses) * 100 : 0,
    exhausted: usesLeft === 0,
  }
}))

// Pass fully used = zero entries left on every service
const isDepleted = computed(() => services.value.length > 0
  && services.value.every(service => service.usesLeft === 0))

// One status per card — the order of conditions defines priority
const status = computed(() => {
  if (isExpired.value) return { key: 'expired', color: 'error', variant: 'tonal' }
  if (isDepleted.value) return { key: 'used', color: 'secondary', variant: 'tonal' }
  if (!props.ticket.isActive) return { key: 'inactive', color: 'secondary', variant: 'tonal' }

  return { key: 'active', color: 'green', variant: 'flat' }
})

const statusColor = computed(() => status.value.color)

// Expired pass with unused entries — those entries are forfeited
const forfeitedUses = computed(() => (isExpired.value
  ? services.value.reduce((sum, service) => sum + service.usesLeft, 0)
  : 0))

const isForfeited = computed(() => forfeitedUses.value > 0)

const lastUsageDate = computed(() => (props.ticket.lastUsage?.date ? new Date(props.ticket.lastUsage.date) : null))

const lastUsageBy = computed(() => props.ticket.lastUsage
  ? (props.ticket.lastUsage.salonerClientId ? props.ticket.lastUsage.usedBy : t('noCustomer'))
  : null)

const usagesExpanded = ref(false)
const usagesEl = ref(null)

const toggleUsages = async () => {
  usagesExpanded.value = !usagesExpanded.value
  if (!usagesExpanded.value) return

  emit('usagesRequest', props.ticket)

  // On a phone the history opens well below the fold. Scrolling right after the element
  // is in the DOM is safe: VExpandTransition grows it downwards, so its top edge - the
  // offset scrollIntoView aims at - does not move while the animation runs.
  if (!mobile.value) return

  await nextTick()
  usagesEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
</script>

<template>
  <VCard
    variant="outlined"
    class="ticket-card border-s-lg"
    :class="{ 'ticket-card--expired': isExpired && !isLoading }"
    :style="isLoading ? undefined : `border-inline-start-color: rgb(var(--v-theme-${statusColor})) !important;`"
    elevation="0"
  >
    <div class="ticket-card__summary d-flex flex-column flex-sm-row flex-wrap flex-md-nowrap">
      <template v-if="isLoading">
        <!-- Mirrors the loaded layout below - same columns, same widths, same wrapping -->
        <div
          class="pa-3 flex-grow-1 d-flex flex-column ga-2"
          style="min-inline-size: 0; max-height: 125px;"
        >
          <VSkeletonLoader
            type="heading"
            width="55%"
            class="ma-0"
          />
          <VSkeletonLoader
            type="text"
            width="35%"
            class="ma-0"
          />
          <VSkeletonLoader
            type="text"
            width="60%"
            class="ma-0"
          />
        </div>

        <VDivider :vertical="mdAndUp" />

        <div
          class="pa-3 d-flex flex-column justify-space-between ga-1"
          style="max-height: 125px;"
          :style="columnStyle"
        >
          <VSkeletonLoader
            type="text"
            width="50%"
          />
          <VSkeletonLoader
            type="button"
            class="ticket-card__skeleton-action"
          />
        </div>

        <VDivider :vertical="smAndUp" />

        <div
          class="ticket-card__usage pa-3 d-flex flex-column justify-space-between ga-1"
          style="max-height: 125px;"
          :style="columnStyle"
        >
          <VSkeletonLoader
            type="text"
            width="50%"
          />
          <VSkeletonLoader
            type="button"
            class="ticket-card__skeleton-action"
          />
        </div>

        <VDivider :vertical="smAndUp" />

        <div
          class="ticket-card__validity pa-3 d-flex flex-column justify-space-between ga-1"
          style="max-height: 125px;"
          :style="columnStyle"
        >
          <VSkeletonLoader
            type="text"
            width="60%"
          />
          <VSkeletonLoader
            type="button"
            class="ticket-card__skeleton-action"
          />
        </div>
      </template>

      <template v-else>
        <!-- ======== IDENTITY + SERVICES ======== -->
        <div
          class="pa-3 flex-grow-1 d-flex flex-column ga-2"
          style="min-inline-size: 0;"
        >
          <div class="d-flex flex-wrap align-center ga-2">
            <span class="text-h6 font-weight-bold text-break">{{ ticket.name || '-' }}</span>
            <VChip
              :color="status.color"
              :variant="status.variant"
              size="x-small"
              label
            >
              {{ $t(`benefit_status.${status.key}`) }}
            </VChip>
            <VChip
              v-if="status.key === 'active' && expiresSoon"
              color="secondary"
              size="x-small"
              variant="tonal"
              label
            >
              {{ expiresSoonLabel }}
            </VChip>
          </div>

          <div
            v-if="ticket.buyer?.name"
            class="text-caption text-medium-emphasis"
          >
            <span>{{ $t('buyer') }}: {{ ticket.buyer.name }}</span>
          </div>

          <!-- Services as compact pills, each with its own consumption bar -->
          <div
            v-if="services.length"
            class="d-flex flex-wrap ga-2"
          >
            <div
              v-for="service in services"
              :key="service.serviceId"
              class="ticket-card__service px-3 py-2 rounded"
            >
              <div class="d-flex align-baseline ga-2">
                <span
                  class="text-body-2 text-break"
                  style="text-wrap: pretty;"
                  :class="service.exhausted ? 'text-disabled' : 'text-high-emphasis'"
                >
                  {{ service.name || '-' }}
                </span>
                <VChip
                  v-if="service.isDeleted"
                  variant="tonal"
                  color="error"
                  size="x-small"
                  label
                >
                  {{ $t('deleted_service') }}
                </VChip>
                <span
                  class="text-body-2 font-weight-bold text-no-wrap ml-auto"
                  :class="service.exhausted ? 'text-disabled' : 'text-high-emphasis'"
                >
                  {{ service.used }}/{{ service.uses }}
                </span>
              </div>
              <VProgressLinear
                :model-value="service.percent"
                :color="service.exhausted ? 'secondary' : 'primary'"
                height="4"
                rounded
                class="mt-1"
              />
            </div>
          </div>
          <div
            v-else
            class="text-caption text-disabled"
          >
            {{ $t('noServicesFound') }}
          </div>
        </div>

        <VDivider :vertical="mdAndUp" />

        <!-- ======== PASS CODE ======== -->
        <div
          class="pa-3 d-flex flex-column justify-space-between ga-1"
          :style="columnStyle"
        >
          <div class="text-body-2 text-uppercase h-100">
            {{ $t('pass_code') }}
          </div>

          <CopyableField
            :value="ticket.identifier"
            size="xx-small"
            monospace
            style="min-inline-size: 100px; max-inline-size: 160px;"
          />
        </div>

        <VDivider :vertical="smAndUp" />

        <!-- ======== LAST USAGE ======== -->
        <div
          class="ticket-card__usage pa-3 d-flex flex-column justify-space-between ga-1"
          :style="columnStyle"
        >
          <div class="d-flex flex-column justify-start h-100">
            <div class="text-body-2 text-uppercase">
              {{ $t('lastUsage') }}
            </div>
            <div
              v-if="lastUsageDate"
              class="d-flex align-center ga-2"
            >
              <span class="text-body-2 text-high-emphasis">{{ extractDate(lastUsageDate) }}</span>
            </div>
            <span
              v-else
              class="text-body-2 text-disabled"
            >—</span>
            <span
              v-if="lastUsageBy"
              class="text-caption text-medium-emphasis text-break"
            >
              {{ lastUsageBy }}
            </span>
          </div>
          <VBtn
            variant="flat"
            color="secondary"
            size="small"
            prepend-icon="ri-history-line"
            :loading="usagesLoading"
            :disabled="!lastUsageDate"
            @click="toggleUsages"
          >
            {{ usagesExpanded ? $t('hideUsageHistory') : $t('showUsageHistory') }}
          </VBtn>
        </div>

        <VDivider :vertical="smAndUp" />

        <!-- ======== VALIDITY ======== -->
        <div
          class="ticket-card__validity pa-3 d-flex flex-column justify-space-between ga-1"
          :style="columnStyle"
        >
          <div class="d-flex flex-column justify-start h-100">
            <div class="text-body-2 text-uppercase">
              {{ $t('validUntil') }}
            </div>
            <span
              class="text-body-2"
              :class="isExpired ? 'text-error' : 'text-high-emphasis'"
            >
              {{ timeoutDate ? extractDate(timeoutDate) : '—' }}
            </span>
            <span
              v-if="isForfeited"
              class="text-caption text-error"
            >
              {{ $t('forfeitedUses', { count: forfeitedUses }) }}
            </span>
            <br>
          </div>
          <VBtn
            v-if="ticket.canUpdateTimeout"
            variant="flat"
            color="secondary"
            size="small"
            prepend-icon="ri-calendar-todo-line"
            @click="emit('editTimeout', ticket)"
          >
            {{ $t('changeDate') }}
          </VBtn>
        </div>
      </template>
    </div>

    <VExpandTransition>
      <div
        v-if="usagesExpanded"
        ref="usagesEl"
        class="ticket-card__usages"
      >
        <VDivider />

        <div class="pa-3">
          <VSkeletonLoader
            v-if="usagesLoading"
            type="paragraph"
          />

          <template v-else>
            <div class="state-strip d-flex flex-wrap align-center justify-space-between ga-2">
              <span class="d-flex align-center ga-2 font-weight-bold">
                <VIcon
                  icon="ri-history-line"
                  size="16"
                />
                <span>{{ t('usage_history') }}</span>
              </span>
            </div>

            <template v-if="usages.length">
              <div
                v-if="!mobile"
                class="usages-table-wrap"
              >
                <table class="usages-table">
                  <thead>
                    <tr>
                      <th
                        class="text-start"
                        style="width: 220px; "
                      >
                        {{ t('date') }} · {{ t('cash_report.hour') }}
                      </th>
                      <th class="text-start">
                        {{ $t('customer') }}
                      </th>
                      <th class="text-start">
                        {{ $t('services_plural') }}
                      </th>
                      <th class="usages-table__action" />
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="usage in usages"
                      :key="usage.id"
                    >
                      <td
                        class="text-no-wrap"
                        style="max-width: 140px"
                      >
                        <div class="d-flex ga-1">
                          <VChip
                            size="x-small"
                            prepend-icon="ri-calendar-2-line"
                          >
                            {{ extractDate(new Date(usage.date)) }}
                          </VChip>

                          <VChip
                            size="x-small"
                            prepend-icon="ri-time-line"
                          >
                            {{ extractTimeFromDateString(usage.date) }}
                          </VChip>
                        </div>
                      </td>
                      <td class="font-weight-medium">
                        {{ usage.salonerClient?.fullName || $t('noCustomer') }}
                      </td>

                      <td>
                        <div
                          v-for="service in usage.services || []"
                          :key="service.id"
                          class="d-flex align-center ga-1"
                        >
                          <VIcon
                            icon="ri-arrow-right-line"
                            size="16"
                          />

                          <span>
                            {{ service.name }} × {{ service.quantity }}
                          </span>
                        </div>
                      </td>

                      <td class="text-end">
                        <VBtn
                          v-if="usage.type === 'events'"
                          variant="flat"
                          color="secondary"
                          size="small"
                          @click.stop="emit('calendarClick', usage)"
                        >
                          <VIcon icon="ri-calendar-event-line" />

                          <VTooltip activator="parent">
                            {{ $t('showOnCalendar') }}
                          </VTooltip>
                        </VBtn>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile -->
              <div
                v-else
                class="d-flex flex-column ga-2"
              >
                <div
                  v-for="usage in usages"
                  :key="usage.id"
                  class="usage-mobile pa-3"
                >
                  <div class="d-flex align-start justify-space-between ga-2">
                    <div
                      class="font-weight-medium text-truncate"
                      style="min-inline-size: 0;"
                    >
                      {{ usage.salonerClient?.fullName || $t('noCustomer') }}
                    </div>
                  </div>

                  <div class="d-flex flex-wrap ga-1 mt-2">
                    <VChip
                      size="x-small"
                      prepend-icon="ri-calendar-2-line"
                    >
                      {{ extractDate(new Date(usage.date)) }}
                    </VChip>

                    <VChip
                      size="x-small"
                      prepend-icon="ri-time-line"
                    >
                      {{ extractTimeFromDateString(usage.date) }}
                    </VChip>
                  </div>
                  <VDivider class="my-2" />

                  <div class="d-flex flex-column ga-1">
                    <div
                      v-for="service in usage.services || []"
                      :key="service.id"
                      class="d-flex align-start ga-1 text-body-2"
                    >
                      <VIcon
                        icon="ri-arrow-right-line"
                        size="16"
                        class="mt-1 flex-shrink-0"
                      />

                      <span>
                        {{ service.name }} × {{ service.quantity }}
                      </span>
                    </div>
                  </div>

                  <VBtn
                    v-if="usage.type === 'events'"
                    variant="flat"
                    color="secondary"
                    size="small"
                    block
                    prepend-icon="ri-calendar-event-line"
                    class="mt-3"
                    @click.stop="emit('calendarClick', usage)"
                  >
                    {{ $t('showOnCalendar') }}
                  </VBtn>
                </div>
              </div>
            </template>

            <div
              v-else
              class="text-caption text-disabled"
            >
              {{ $t('noResultsFound') }}
            </div>
          </template>
        </div>
      </div>
    </VExpandTransition>
  </VCard>
</template>

<style lang="scss" scoped>
.ticket-card--expired {
  opacity: 0.9;
  filter: grayscale(50%);
}

.ticket-card__usages {
  //flex-basis: 100%;
  inline-size: 100%;
}

.ticket-card__service {
  //min-inline-size: 200px;
  //max-inline-size: 340px;
  border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.03);
  @media(min-width: 1440px) {
    min-inline-size: 200px;
    max-inline-size: 800px;
  }
}

// The action skeletons stand in for 34px buttons / the copy field, not Vuetify's 70px chip
.ticket-card__skeleton-action :deep(.v-skeleton-loader__button) {
  block-size: 34px;
  inline-size: 100%;
  max-inline-size: none;
}

// Between the phone stack and the md row layout the data columns still fit side by side -
// only the identity block takes a line of its own.
@media (min-width: 600px) and (max-width: 959.98px) {
  .ticket-card__summary > div {
    flex: 1 1 170px;
  }

  .ticket-card__summary > .flex-grow-1 {
    flex-basis: 100%;
  }

  // Below md the dividers render horizontally - inside the wrapped row they have to stand
  // back up. The first one keeps its full-width form: it is what cuts the identity block
  // off from the data columns underneath.
  .ticket-card__summary > .v-divider:not(:first-of-type) {
    flex: 0 0 auto;
    align-self: stretch;
    block-size: auto;
    inline-size: 0;
    border-width: 0 thin 0 0;
  }
}

.ticket-card__usage {
  background: rgba(var(--v-theme-on-surface), 0.02);
}

.ticket-card__validity {
  background: rgba(var(--v-theme-on-surface), 0.03);
}

.usages-table-wrap {
  overflow-x: auto;
}

.usages-table {
  inline-size: 100%;
  min-inline-size: 640px;
  border-collapse: collapse;

  th {
    padding: 6px 8px;
    border-block-end: 1px solid rgba(
      var(--v-border-color),
      var(--v-border-opacity)
    );
    color: rgba(
      var(--v-theme-on-surface),
      var(--v-disabled-opacity)
    );
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
  }

  td {
    padding: 8px;
    border-block-end: 1px solid rgba(
      var(--v-border-color),
      var(--v-border-opacity)
    );
    font-size: 0.8125rem;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-block-end: none;
  }
}

.usages-table__action {
  inline-size: 48px;
}
.usage-mobile {
  border: 1px solid rgba(
    var(--v-border-color),
    var(--v-border-opacity)
  );
  border-radius: 8px;
  background-color: rgba(var(--v-theme-on-surface), 0.02);
}
.state-strip {
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  background-color: rgba(var(--v-theme-on-surface), 0.12);
}
</style>

